import { lineupSize } from "../../constants";
import { currencyFormatter, pointsFormatter } from "../../helpers";
import { Uid } from "../../types";
import styles from "./ArtistLineUpComponent.module.css";
import { JSX, useMemo } from "react";
import downIcon from "../../assets/material-symbol-arrow-drop-down.svg";
import upIcon from "../../assets/material-symbol-arrow-drop-up.svg";
import { globals, images, utilities } from "../../styles";
import {
  plus_Icon,
  artist_Icon,
  star_Icon,
  star_boosted_Icon,
} from "../../assets/icons";
import { ApiArtist } from "../../api/dataTypes";

const addArtistPlaceholder = "add-artist-placeholder";

function ArtistLineUpComponent(props: {
  artists: ApiArtist[];
  favouriteArtists?: ApiArtist[];
  handleArtistClicked: (id: Uid) => void;
  handleArtistDropped?: (id: Uid) => void;
  boostedArtist?: Uid;
  handleArtistBoost?: (id: Uid | undefined) => void;
  handleAddArtist?: () => void;
  style?: React.CSSProperties;
}) {
  const {
    artists,
    favouriteArtists,
    handleArtistClicked,
    handleArtistDropped,
    boostedArtist,
    handleArtistBoost,
    handleAddArtist,
    style,
  } = props;

  const sortedArtists = useMemo<ApiArtist[]>(
    () =>
      artists.sort(
        (a, b) => b.artistPrice.currentPrice - a.artistPrice.currentPrice,
      ),
    [artists],
  );

  function renderLineupEntry(entry: ApiArtist | string) {
    const isPlaceholder =
      typeof entry === "string" && entry.includes(addArtistPlaceholder);
    const isFavourite =
      typeof entry !== "string" && favouriteArtists?.includes(entry);
    let textContent = "",
      artistId = "";
    let points = 0,
      artistCost = 0,
      previousCost = 0;
    let costClass = "";
    let costIconSrc = "";
    let handleDropClicked, handleClicked, handleBoostClicked;
    if (isPlaceholder) {
      textContent = entry;
      handleClicked = handleAddArtist;
      artistId = entry;
    } else if (typeof entry === "string") {
      throw new Error(
        "[ArtistLineUpComponent] Invalid argument passed to renderLineupEntry",
      );
    } else {
      textContent = entry.name;
      artistId = entry.id;
      points = entry.artistPoints.totalPoints;
      artistCost = entry.artistPrice.currentPrice;
      previousCost =
        entry.artistPrice.previousPrice ?? entry.artistPrice.currentPrice;
      if (previousCost && artistCost - previousCost > 0) {
        costClass = "cost--green";
        costIconSrc = upIcon;
      } else if (previousCost && artistCost - previousCost < 0) {
        costClass = "cost--red";
        costIconSrc = downIcon;
      }
      if (handleArtistDropped) {
        handleDropClicked = (e?: React.MouseEvent) => {
          e?.stopPropagation();
          handleArtistDropped(artistId);
        };
      }
      handleClicked = () => handleArtistClicked(artistId);
      handleBoostClicked = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (!handleArtistBoost) {
          return;
        }
        if (isArtistBoosted) {
          handleArtistBoost(undefined);
        } else {
          handleArtistBoost(artistId);
        }
      };
    }

    const isArtistBoosted = artistId === boostedArtist;
    const canArtistBeBoosted = !!handleArtistBoost;
    const showBoostIcon =
      (canArtistBeBoosted || isArtistBoosted) && !isFavourite;

    const placeholderContent = (
      <div className={`${utilities["flex-row"]} `} style={{ width: "100%" }}>
        <img src={plus_Icon} className={images["plus-icon-list"]} alt="" />
        <img src={artist_Icon} className={images["artist-icon-list"]} alt="" />
      </div>
    );
    return (
      <button
        onClick={handleClicked}
        className={globals["button-wrapper"]}
        key={artistId}
      >
        <div
          className={`${styles["entry-button"]} ${styles["entry"]} ${utilities["flex-row"]} ${utilities["space-between"]} ${
            isPlaceholder || isFavourite ? styles["entry--temp"] : ""
          }`}
        >
          {isPlaceholder ? (
            placeholderContent
          ) : (
            <>
              <div
                className={`${utilities["flex-col"]} ${styles["entry-info"]}`}
              >
                <div className={styles["artist-name"]}>{textContent}</div>
                <div
                  className={`${utilities["flex-row"]} ${utilities["gap-5"]}`}
                >
                  <div
                    className={`${styles["cost"]} ${styles[costClass]} ${utilities["flex-row"]} ${utilities["gap-2"]}`}
                  >
                    {currencyFormatter(artistCost)}
                    {costIconSrc && (
                      <img
                        src={costIconSrc}
                        alt="costIcon"
                        style={{ height: "auto" }}
                      />
                    )}
                  </div>
                  <div className={styles["points"]}>/</div>
                  <div className={styles["points"]}>
                    {pointsFormatter(points)} pts
                  </div>
                </div>
              </div>
              <div
                className={`${utilities["flex-col"]} ${styles["entry-actions"]}`}
              >
                {showBoostIcon && (
                  <button
                    className={globals["button-wrapper"]}
                    onClick={handleBoostClicked}
                  >
                    <img
                      src={isArtistBoosted ? star_boosted_Icon : star_Icon}
                      alt="boost"
                      className={styles["boost-icon"]}
                    />
                  </button>
                )}
                {!!handleDropClicked && !isFavourite && (
                  <button
                    className={globals["button-wrapper"]}
                    onClick={handleDropClicked}
                  >
                    <div className={styles["drop-button"]}>Drop Artist?</div>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </button>
    );
  }

  function renderPlaceholderEntries() {
    const entries: JSX.Element[] = [];
    const placeholdersRequired =
      lineupSize - (sortedArtists.length + (favouriteArtists?.length ?? 0));
    for (let i = 0; i < placeholdersRequired; i++) {
      entries.push(renderLineupEntry(`${addArtistPlaceholder}-${i}`));
    }
    return entries;
  }

  return (
    <div
      className={`${styles["artist-lineup"]} ${utilities["flex-center"]} ${utilities["flex-gap"]}`}
      style={style}
    >
      {sortedArtists.map(renderLineupEntry)}
      {favouriteArtists?.map(renderLineupEntry)}
      {handleAddArtist &&
        sortedArtists.length < lineupSize &&
        renderPlaceholderEntries()}
    </div>
  );
}

export default ArtistLineUpComponent;
